import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { reportDraftKey, readReportDraft, writeReportDraft } from '@/utils/reportDraft';
import { reportVehicleHeading } from '@/utils/reportVehicle';
import { hasProFeatures } from '@/utils/entitlements';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, Badge, VehicleImage } from '@/components';
import { VehicleDataInjectionPanel } from '@/components/report/VehicleDataInjectionPanel';
import {
  useVehicle,
  useSession,
  useSessions,
  useEntitlement,
  useGenerateReport,
  useRefineReport,
  useFinalizeReport,
} from '@/hooks/useProApi';
import { utcToLocal, type SessionSummary, type InjectedVehicleData } from '@/types';
import { SignInAgain } from '@/components/SignInAgain';

// Strip ```markdown ... ``` code fences that AI models often wrap around output
function stripCodeFence(md: string): string {
  return md.replace(/^```(?:markdown|md)?\s*\n?/, '').replace(/\n?```\s*$/, '');
}

const reportTypeLabels: Record<string, string> = {
  emissions: 'Emissions',
  diagnostic: 'Diagnostic',
  pre_purchase: 'Pre-Purchase',
  estimate: 'Estimate',
};

export function ReportGenerate() {
  const { user, isAuthenticated } = useAuth0();
  const [params] = useSearchParams();
  const subject = isAuthenticated ? user?.sub : undefined;
  if (!subject) return <Card><p>Sign in to open your report workspace.</p></Card>;
  return <OwnedReportGenerate key={JSON.stringify([reportDraftKey(subject, params.get('vin') || '', params.get('type') || 'diagnostic'), params.get('sessionKey') || ''])} subject={subject} />;
}

function OwnedReportGenerate({ subject }: { subject: string }) {
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const vin = params.get('vin') || '';
  const reportType = (params.get('type') || 'diagnostic') as 'emissions' | 'diagnostic' | 'pre_purchase' | 'estimate';
  const initialSessionKey = params.get('sessionKey') || '';

  const { data: entitlement, isLoading: entitlementLoading, error: entitlementError } = useEntitlement();
  const isPro = hasProFeatures(entitlement?.plan);

  // Restore state from sessionStorage if available (round-trip from PDF viewer)
  const storageKey = reportDraftKey(subject, vin, reportType);
  const [savedDraft] = useState(() => readReportDraft(sessionStorage, subject, vin, reportType, initialSessionKey));

  const [activeSessionKey, setActiveSessionKey] = useState(savedDraft?.activeSessionKey || initialSessionKey);
  const [markdown, setMarkdown] = useState(savedDraft?.markdown || '');
  const [refineInput, setRefineInput] = useState('');
  const [debouncedMarkdown, setDebouncedMarkdown] = useState(savedDraft?.markdown || '');

  const { data: vehicle, isLoading: vehicleLoading } = useVehicle(vin);
  const { data: activeSession, isLoading: sessionLoading } = useSession(activeSessionKey);
  const { data: sessionsData } = useSessions({ vin, pageSize: 100 });
  const sessions = sessionsData?.sessions ?? [];

  const [injectedData, setInjectedData] = useState<InjectedVehicleData | undefined>();
  const handleInjectedDataChange = useCallback((data: InjectedVehicleData | undefined) => {
    setInjectedData(data);
  }, []);

  const generateMutation = useGenerateReport();
  const refineMutation = useRefineReport();
  const finalizeMutation = useFinalizeReport();

  // Persist draft state to sessionStorage for round-trip from PDF viewer
  useEffect(() => {
    if (markdown && activeSessionKey) {
      writeReportDraft(sessionStorage, subject, vin, reportType, { markdown, activeSessionKey });
    }
  }, [markdown, activeSessionKey, storageKey, subject, vin, reportType]);

  // Debounce markdown for preview
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMarkdown(markdown), 500);
    return () => clearTimeout(t);
  }, [markdown]);

  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Auto-generate if sessionKey in URL and no saved draft
  useEffect(() => {
    if (initialSessionKey && !markdown && !savedDraft && !generateMutation.isPending) {
      generateMutation.mutate(
        { sessionKey: initialSessionKey, reportType, injectedData, clientTimezone },
        { onSuccess: (data) => { if (mounted.current) setMarkdown(stripCodeFence(data.markdown)); } }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionKey]);

  function handleGenerate(sessionKey: string) {
    setActiveSessionKey(sessionKey);
    generateMutation.mutate(
      { sessionKey, reportType, injectedData, clientTimezone },
      { onSuccess: (data) => { if (mounted.current) setMarkdown(stripCodeFence(data.markdown)); } }
    );
  }

  function handleRefine() {
    if (!refineInput.trim() || !markdown || !activeSessionKey) return;
    refineMutation.mutate(
      { sessionKey: activeSessionKey, currentMarkdown: markdown, instructions: refineInput },
      {
        onSuccess: (data) => {
          if (!mounted.current) return;
          setMarkdown(stripCodeFence(data.markdown));
          setRefineInput('');
        },
      }
    );
  }

  function handleFinalize() {
    if (!markdown || !activeSessionKey) return;
    finalizeMutation.mutate(
      {
        sessionKey: activeSessionKey,
        vin,
        vehicleYear: vehicle?.vehicleYear ?? '',
        vehicleMake: vehicle?.vehicleMake ?? '',
        vehicleModel: vehicle?.vehicleModel ?? '',
        reportType,
        markdown,
        clientTimezone,
      },
      { onSuccess: (data) => { if (mounted.current) navigate(`/reports/${data.reportId}`); } }
    );
  }

  const vehicleName = reportVehicleHeading({
    vin, activeSessionKey, session: activeSession,
    summary: sessions.find(session => session.sessionKey.toLowerCase() === activeSessionKey.toLowerCase()),
    vehicle, sessionLoading, vehicleLoading, hasReport: !!markdown,
  });

  // Auth failure is not 'no subscription': route back through sign-in (#164)
  if (entitlementError) {
    return <SignInAgain error={entitlementError} />;
  }

  // Pro gate — block non-Pro users from report generation
  if (!entitlementLoading && !isPro) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md text-center">
          <div className="py-8 px-4">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#6d7cff] to-[#e866dd] flex items-center justify-center opacity-50">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-3">Pro Feature</h2>
            <p className="text-gray-400 leading-relaxed mb-6">
              Report generation requires a Pro subscription.
            </p>
            <Link
              to={vin ? `/vehicles/${vin}` : '/reports'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-obdai-deep text-gray-300 rounded-xl hover:bg-white/10 transition font-medium"
            >
              Go Back
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={vin ? `/vehicles/${vin}` : '/reports'}
          className="text-gray-400 hover:text-white transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <VehicleImage vin={vin} size="md" />
        <div>
          <h1 className="text-xl font-bold text-white">
            Generate {reportTypeLabels[reportType] || 'Diagnostic'} Report
          </h1>
          <p className="text-gray-400">{vehicleName}</p>
        </div>
      </div>

      {/* Vehicle Intelligence Data Injection — shown standalone before report is generated */}
      {!markdown && !generateMutation.isPending && (
        <VehicleDataInjectionPanel
          vin={vin}
          reportType={reportType}
          onChange={handleInjectedDataChange}
        />
      )}

      {/* Two-region layout: top = editor+preview, bottom = injection panel */}
      {(markdown || generateMutation.isPending) ? (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 10rem)' }}>
          {/* TOP REGION — Editor (left) + Preview (right) */}
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT — Markdown Editor (raw div for proper flex) */}
            <div className="bg-obdai-primary rounded-xl p-6 flex flex-col min-h-0 overflow-hidden">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 px-1 flex-shrink-0">Markdown Editor</p>
              {generateMutation.isPending ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-obdai-gradient-start mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">AI is generating your report...</p>
                  </div>
                </div>
              ) : (
                <>
                  <textarea
                    value={markdown}
                    onChange={(e) => setMarkdown(e.target.value)}
                    className="flex-1 min-h-0 w-full bg-black/30 text-gray-200 border border-gray-700/50 rounded-lg p-4 font-mono text-sm resize-none focus:outline-none focus:border-[#6d7cff]/50"
                  />
                  {/* Refine input */}
                  <div className="mt-3 flex gap-2 flex-shrink-0">
                    <input
                      type="text"
                      value={refineInput}
                      onChange={(e) => setRefineInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRefine()}
                      placeholder="Tell AI to make changes..."
                      className="flex-1 bg-black/30 text-gray-200 border border-gray-700/50 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#6d7cff]/50"
                    />
                    <button
                      onClick={handleRefine}
                      disabled={refineMutation.isPending || !refineInput.trim() || !activeSessionKey}
                      className="px-4 py-2 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white text-sm font-medium rounded-lg disabled:opacity-50 transition"
                    >
                      {refineMutation.isPending ? 'Refining...' : 'Send'}
                    </button>
                  </div>
                  {/* Action buttons */}
                  <div className="mt-3 flex items-center justify-center gap-3 flex-shrink-0">
                    <button
                      onClick={() => activeSessionKey && handleGenerate(activeSessionKey)}
                      disabled={generateMutation.isPending || !activeSessionKey}
                      className="px-5 py-2 bg-obdai-deep text-gray-300 rounded-lg hover:bg-white/10 transition text-sm font-medium disabled:opacity-50"
                    >
                      Regenerate
                    </button>
                    <button
                      onClick={handleFinalize}
                      disabled={finalizeMutation.isPending || !markdown || !activeSessionKey}
                      className="px-5 py-2 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white rounded-lg hover:shadow-lg hover:shadow-purple-500/20 transition text-sm font-medium disabled:opacity-50"
                    >
                      {finalizeMutation.isPending ? 'Generating PDF...' : 'Finalize Report'}
                    </button>
                  </div>
                  {finalizeMutation.isError && (
                    <p className="text-red-400 text-center text-xs mt-1">
                      Failed to finalize report. Please try again.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* RIGHT — HTML Preview (raw div for proper flex) */}
            <div className="bg-obdai-primary rounded-xl p-6 flex flex-col min-h-0 overflow-hidden">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 px-1 flex-shrink-0">Preview</p>
              <div className="flex-1 min-h-0 overflow-y-auto bg-white rounded-lg p-6 report-preview">
                {debouncedMarkdown ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{debouncedMarkdown}</ReactMarkdown>
                ) : (
                  <p className="text-gray-400 italic">Preview will appear here...</p>
                )}
              </div>
            </div>
          </div>

          {/* BOTTOM REGION — Injection Panel */}
          <div className="flex-shrink-0 mt-4 max-h-[40vh] overflow-y-auto">
            <VehicleDataInjectionPanel
              vin={vin}
              reportType={reportType}
              onChange={handleInjectedDataChange}
            />
          </div>
        </div>
      ) : null}

      {/* Session list — only shown before report is generated */}
      {!markdown && !generateMutation.isPending && (
      <Card>
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">
          Session History for {vehicleName}
        </p>
        {sessions.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No sessions found for this vehicle.</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {sessions.map((s: SessionSummary, idx: number) => (
              <div
                key={`${s.sessionKey}-${idx}`}
                className={`flex items-center justify-between p-3 rounded-lg transition ${
                  activeSessionKey === s.sessionKey
                    ? 'bg-[#6d7cff]/10 border border-[#6d7cff]/30'
                    : 'bg-obdai-deep hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-white text-sm font-medium">
                      {format(utcToLocal(s.createdAt), 'MMM d, yyyy h:mm a')}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={s.dtcCount > 0 ? 'error' : 'success'}>
                        {s.dtcCount} DTC{s.dtcCount !== 1 ? 's' : ''}
                      </Badge>
                      {s.milStatus !== null && (
                        <Badge variant={s.milStatus ? 'error' : 'success'}>
                          MIL {s.milStatus ? 'ON' : 'OFF'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleGenerate(s.sessionKey)}
                  disabled={generateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition disabled:opacity-50 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white hover:shadow-lg hover:shadow-purple-500/20"
                >
                  {generateMutation.isPending && activeSessionKey === s.sessionKey
                    ? 'Generating...'
                    : 'Generate Report'}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
